import { useMemo, useState } from 'react';
import type { PostFilterState } from './applyPostFilters';

/**
 * Phase F — old2 `FilterContext` の filter state を v2 の hoist-by-page パターンで再現する hook。
 *
 * old2 は FilterProvider が posts を保持して filteredPosts を context 配布していたが、v2 は
 * usePosts の購読が Page (AppHome / MapPage) ごとに独立しているため、filter「状態」のみを本 hook で
 * 集約し、適用 (applyPostFilters) は各 Page が posts と組み合わせて行う。
 *
 * 状態は localStorage 永続化しない (画面遷移で消える、old2 と同じ in-memory)。
 * tab 切替 (post ⇄ list) は AppHome 内なので本 hook の値は保持されるが、map ⇄ list の Page 跨ぎでは
 * リセットされる (old2 も MainPage ↔ tabs 内で 1 Provider のため挙動は近似。完全共有が必要なら
 * 将来 URL / context へ昇格)。
 */
export interface MainFilters {
  keyword: string;
  setKeyword: (v: string) => void;
  sessionIds: string[];
  setSessionIds: (ids: string[]) => void;
  /**
   * 現セッション(=メインセッション)のみ表示する opt-in フィルタ (旧 REQ-4.1.20)。
   * 案1 統合 (2026-06-16) で「現セッション」が membership.mainSessionId として常に解決されるように
   * なったため、default ON のままだと一覧/地図が常にメインセッションへ自動で絞り込まれ (他/未紐付け
   * セッションの投稿が消え、フィルタバッジが常時 ① 点灯) てしまう。これは「何も操作していないのに
   * フィルタが効く」誤体験になるため **default false** (= 既定は全件表示・バッジ 0) に変更し、
   * ユーザーが明示的に ON にしたときだけメインセッションへ絞り込む。
   */
  filterBySession: boolean;
  setFilterBySession: (v: boolean) => void;
  dateFrom: string;
  setDateFrom: (v: string) => void;
  dateTo: string;
  setDateTo: (v: string) => void;
  hasPhoto: boolean | null;
  setHasPhoto: (v: boolean | null) => void;
  minAgreeCount: number;
  setMinAgreeCount: (v: number) => void;
  onlyMine: boolean;
  setOnlyMine: (v: boolean) => void;
  postFieldFilters: Record<string, string[]>;
  setPostFieldFilters: (next: Record<string, string[]>) => void;
  userFieldFilters: Record<string, string[]>;
  setUserFieldFilters: (next: Record<string, string[]>) => void;
  selectedCategoryCodes: string[];
  setSelectedCategoryCodes: (codes: string[]) => void;
  /** 全 filter を初期値に戻す (old2 resetFilter)。 */
  reset: () => void;
  /**
   * applyPostFilters に渡す state を組み立てる (currentSessionId / currentUid は Page から注入)。
   */
  toFilterState: (
    currentSessionId: string | null,
    currentUid: string | null,
    selfUids?: string[],
  ) => PostFilterState;
}

export function useMainFilters(): MainFilters {
  const [keyword, setKeyword] = useState('');
  const [sessionIds, setSessionIds] = useState<string[]>([]);
  // 案1 統合: メインセッション常時解決に伴い default OFF (詳細は MainFilters.filterBySession の doc)。
  const [filterBySession, setFilterBySession] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [hasPhoto, setHasPhoto] = useState<boolean | null>(null);
  const [minAgreeCount, setMinAgreeCount] = useState(0);
  const [onlyMine, setOnlyMine] = useState(false);
  const [postFieldFilters, setPostFieldFilters] = useState<Record<string, string[]>>({});
  const [userFieldFilters, setUserFieldFilters] = useState<Record<string, string[]>>({});
  const [selectedCategoryCodes, setSelectedCategoryCodes] = useState<string[]>([]);

  return useMemo(
    () => ({
      keyword,
      setKeyword,
      sessionIds,
      setSessionIds,
      filterBySession,
      setFilterBySession,
      dateFrom,
      setDateFrom,
      dateTo,
      setDateTo,
      hasPhoto,
      setHasPhoto,
      minAgreeCount,
      setMinAgreeCount,
      onlyMine,
      setOnlyMine,
      postFieldFilters,
      setPostFieldFilters,
      userFieldFilters,
      setUserFieldFilters,
      selectedCategoryCodes,
      setSelectedCategoryCodes,
      reset: () => {
        setKeyword('');
        setSessionIds([]);
        // 案1 統合: reset も default(false) に戻す (現セッション絞り込みは opt-in)。
        setFilterBySession(false);
        setDateFrom('');
        setDateTo('');
        setHasPhoto(null);
        setMinAgreeCount(0);
        setOnlyMine(false);
        setPostFieldFilters({});
        setUserFieldFilters({});
        setSelectedCategoryCodes([]);
      },
      toFilterState: (currentSessionId, currentUid, selfUids) => ({
        filterBySession,
        currentSessionId,
        dateFrom,
        dateTo,
        selectedCategoryCodes,
        keyword,
        sessionIds,
        minAgreeCount,
        hasPhoto,
        onlyMine,
        currentUid,
        // R-70: 引き継ぎ後も旧端末投稿を onlyMine に含める (省略時は applyPostFilters が currentUid 単独へ fallback)
        selfUids,
        postFieldFilters,
        userFieldFilters,
      }),
    }),
    [
      keyword,
      sessionIds,
      filterBySession,
      dateFrom,
      dateTo,
      hasPhoto,
      minAgreeCount,
      onlyMine,
      postFieldFilters,
      userFieldFilters,
      selectedCategoryCodes,
    ],
  );
}
